import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { PremiumBadge } from '../../components/ui/premium-badge';
import { PremiumUpgradeDialog } from '../../components/ui/premium-overlay';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency, formatDate } from '../../lib/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  ArrowLeft,
  Download,
  Trash2,
  User,
  Wrench,
  CheckCircle,
  CreditCard,
  History,
  AlertCircle,
  Share2,
  MessageCircle,
  Printer,
  ChevronDown,
  Phone,
  Split,
  XCircle,
  Settings2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  paid: 'bg-green-100 text-green-800 border-green-300',
  partial: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  unpaid: 'bg-red-100 text-red-800 border-red-300'
};

const statusLabelsMap = {
  en: { paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid' },
  bn: { paid: 'পরিশোধিত', partial: 'আংশিক', unpaid: 'অপরিশোধিত' }
};

const paymentMethodLabels = {
  cash: 'Cash',
  bkash: 'bKash',
  nagad: 'Nagad',
  bank: 'Bank Transfer',
  card: 'Card',
  mixed: 'Mixed'
};

export default function InvoiceDetailPage() {
  const { id, garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { garageInfo, isPremium } = useAuth();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  const [invoice, setInvoice] = useState(null);
  const [customerPoints, setCustomerPoints] = useState(null);
  const [loading, setLoading] = useState(true);

  // Record Payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [splitPayment, setSplitPayment] = useState(false);
  const [splitMethod2, setSplitMethod2] = useState('bkash');
  const [splitAmount2, setSplitAmount2] = useState('');

  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareProgress, setShareProgress] = useState('');

  // Waive state
  const [showWaiveDialog, setShowWaiveDialog] = useState(false);
  const [waiving, setWaiving] = useState(false);

  // Premium upgrade popup
  const [upgradePopup, setUpgradePopup] = useState({ open: false, feature: '' });

  // Thermal paper size
  const [thermalPaperSize, setThermalPaperSize] = useState(80);

  // WhatsApp phone dialog state
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [whatsAppPhone, setWhatsAppPhone] = useState('');
  const [additionalPhones, setAdditionalPhones] = useState([]);

  useEffect(() => {
    fetchInvoice();
    // Fetch thermal paper size from garage settings
    axios.get(`${API}/settings/garage`).then(res => {
      setThermalPaperSize(res.data.thermal_paper_size || 80);
    }).catch(() => {});
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`);
      setInvoice(response.data);
      if (response.data?.customer_id) {
        try {
          const pointsRes = await axios.get(`${API}/marketing/loyalty/customers/${response.data.customer_id}/points`);
          setCustomerPoints(pointsRes.data || null);
        } catch {
          setCustomerPoints(null);
        }
      } else {
        setCustomerPoints(null);
      }
    } catch (error) {
      toast({ title: 'Invoice not found', variant: 'destructive' });
      navigate(`${basePath}/invoices`);
    } finally {
      setLoading(false);
    }
  };

  // Helper: show updating progress toast
  const withProgress = async (steps, action) => {
    setSharing(true);
    setShareProgress(steps[0]);
    const progressToast = toast({ title: steps[0], duration: 30000 });
    try {
      const updateStep = (idx) => {
        if (idx < steps.length) {
          setShareProgress(steps[idx]);
          progressToast.update({ title: steps[idx] });
        }
      };
      await action(updateStep);
      progressToast.dismiss();
    } catch (error) {
      progressToast.dismiss();
      throw error;
    } finally {
      setSharing(false);
      setShareProgress('');
    }
  };

  const downloadPdf = async () => {
    try {
      await withProgress(
        language === 'en'
          ? ['Generating PDF...', 'Downloading...', 'Done!']
          : ['PDF তৈরি হচ্ছে...', 'ডাউনলোড হচ্ছে...', 'সম্পন্ন!'],
        async (updateStep) => {
          const response = await axios.get(`${API}/invoices/${id}/pdf`, {
            responseType: 'blob'
          });
          updateStep(1);
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          updateStep(2);
        }
      );
      toast({ title: language === 'en' ? 'PDF Downloaded!' : 'PDF ডাউনলোড হয়েছে!' });
    } catch (error) {
      toast({ title: 'Failed to download PDF', variant: 'destructive' });
    }
  };

  const openWhatsAppDialog = async () => {
    setWhatsAppPhone(invoice?.customer_phone || '');
    // Fetch customer's additional phones
    if (invoice?.customer_id) {
      try {
        const res = await axios.get(`${API}/customers/${invoice.customer_id}`);
        setAdditionalPhones(res.data.additional_phones || []);
      } catch { /* ignore */ }
    }
    setShowWhatsAppDialog(true);
  };

  const shareViaWhatsApp = async (phoneNumber) => {
    setShowWhatsAppDialog(false);
    try {
      await withProgress(
        language === 'en'
          ? ['Preparing share link...', 'Opening WhatsApp...']
          : ['শেয়ার লিংক তৈরি হচ্ছে...', 'হোয়াটসঅ্যাপ খুলছে...'],
        async (updateStep) => {
          const linkResponse = await axios.post(`${API}/invoices/${id}/share-link`);
          const { message } = linkResponse.data;

          let phone = (phoneNumber || '').replace(/[^\d+]/g, '');
          if (phone.startsWith('0')) {
            phone = '88' + phone;
          } else if (!phone.startsWith('+') && !phone.startsWith('88')) {
            phone = '88' + phone;
          }
          phone = phone.replace('+', '');

          updateStep(1);
          const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
          window.open(waUrl, '_blank');
        }
      );
      // Save the number if it differs from primary phone
      if (invoice?.customer_id && phoneNumber !== invoice.customer_phone) {
        try {
          const res = await axios.post(`${API}/customers/${invoice.customer_id}/phones`, { phone: phoneNumber });
          setAdditionalPhones(res.data.additional_phones || []);
        } catch { /* ignore */ }
      }
    } catch (error) {
      console.error('Share error:', error);
      toast({ title: language === 'en' ? 'Failed to share' : 'শেয়ার ব্যর্থ', variant: 'destructive' });
    }
  };

  const shareViaApps = async () => {
    try {
      await withProgress(
        language === 'en'
          ? ['Preparing invoice...', 'Opening share menu...']
          : ['ইনভয়েস তৈরি হচ্ছে...', 'শেয়ার মেনু খুলছে...'],
        async (updateStep) => {
          // Start both PDF download and share link generation in parallel
          const linkPromise = axios.post(`${API}/invoices/${id}/share-link`);
          const pdfPromise = axios.get(`${API}/invoices/${id}/pdf`, {
            responseType: 'blob',
            timeout: 4000, // 4s timeout for PDF
          }).catch(() => null); // swallow timeout/error

          const [linkResponse, pdfResponse] = await Promise.all([linkPromise, pdfPromise]);
          const { share_url } = linkResponse.data;
          const invNum = invoice?.invoice_number || 'Invoice';

          updateStep(1);

          if (navigator.share) {
            // Try sharing PDF file if we got it in time
            if (pdfResponse?.data && navigator.canShare) {
              const file = new File(
                [pdfResponse.data],
                `invoice_${invNum}.pdf`,
                { type: 'application/pdf' }
              );
              const shareData = {
                title: `Invoice ${invNum}`,
                files: [file],
              };
              if (navigator.canShare(shareData)) {
                await navigator.share(shareData);
                return;
              }
            }
            // Fallback: share URL link
            await navigator.share({
              title: `Invoice ${invNum}`,
              text: `Invoice ${invNum} from ${garageInfo?.name || 'MotoPotti Garage'}`,
              url: share_url,
            });
          } else {
            // Fallback: copy to clipboard
            await navigator.clipboard.writeText(share_url);
            toast({ title: language === 'en' ? 'Link copied to clipboard!' : 'লিংক কপি হয়েছে!' });
          }
        }
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        toast({ title: language === 'en' ? 'Failed to share' : 'শেয়ার ব্যর্থ', variant: 'destructive' });
      }
    }
  };

  const printThermal = async () => {
    try {
      await withProgress(
        language === 'en'
          ? ['Generating receipt...', 'Opening print dialog...']
          : ['রিসিট তৈরি হচ্ছে...', 'প্রিন্ট ডায়ালগ খুলছে...'],
        async (updateStep) => {
          const response = await axios.get(`${API}/invoices/${id}/thermal-pdf?paper_size=${thermalPaperSize}`, {
            responseType: 'blob'
          });
          updateStep(1);
          const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(pdfBlob);
          const printWindow = window.open(url, '_blank');
          if (printWindow) {
            printWindow.addEventListener('load', () => {
              printWindow.print();
            });
          }
        }
      );
      toast({ title: language === 'en' ? 'Thermal receipt ready' : 'থার্মাল রিসিট প্রস্তুত' });
    } catch (error) {
      console.error('Thermal print error:', error);
      toast({ title: language === 'en' ? 'Failed to generate receipt' : 'রিসিট তৈরি ব্যর্থ', variant: 'destructive' });
    }
  };

  const deleteInvoice = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast({ title: language === 'en' ? 'Invoice deleted' : 'ইনভয়েস মুছে ফেলা হয়েছে' });
      navigate(`${basePath}/invoices`);
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to delete invoice' : 'ইনভয়েস মুছে ফেলা ব্যর্থ', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const waiveInvoiceDue = async () => {
    setWaiving(true);
    try {
      await axios.put(`${API}/invoices/${id}/waive`);
      toast({ title: language === 'en' ? 'Due waived off successfully' : 'বকেয়া মওকুফ হয়েছে' });
      setShowWaiveDialog(false);
      fetchInvoice();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to waive off' : 'মওকুফ ব্যর্থ', variant: 'destructive' });
    } finally {
      setWaiving(false);
    }
  };

  const recordPayment = async () => {
    const amount1 = parseInt(paymentAmount) || 0;
    if (amount1 <= 0) {
      toast({ title: language === 'en' ? 'Enter a valid amount' : 'সঠিক পরিমাণ লিখুন', variant: 'destructive' });
      return;
    }
    if (splitPayment) {
      const amount2 = parseInt(splitAmount2) || 0;
      if (amount2 <= 0) {
        toast({ title: language === 'en' ? 'Enter a valid second amount' : 'দ্বিতীয় পরিমাণ সঠিক নয়', variant: 'destructive' });
        return;
      }
      const total = amount1 + amount2;
      if (total > invoice.due_amount) {
        toast({ title: language === 'en' ? 'Total exceeds due balance' : 'মোট বকেয়ার চেয়ে বেশি', variant: 'destructive' });
        return;
      }
      setSubmittingPayment(true);
      try {
        await axios.post(`${API}/invoices/${id}/payments`, {
          amount: amount1,
          payment_method: paymentMethod,
          notes: paymentNotes ? `${paymentNotes} (split 1/2)` : 'Split payment 1/2'
        });
        const response = await axios.post(`${API}/invoices/${id}/payments`, {
          amount: amount2,
          payment_method: splitMethod2,
          notes: paymentNotes ? `${paymentNotes} (split 2/2)` : 'Split payment 2/2'
        });
        setInvoice(response.data);
        setShowPaymentForm(false);
        setPaymentAmount('');
        setSplitAmount2('');
        setPaymentNotes('');
        setSplitPayment(false);
        toast({ title: language === 'en' ? 'Split payment recorded!' : 'বিভক্ত পেমেন্ট রেকর্ড হয়েছে!' });
      } catch (error) {
        toast({ title: language === 'en' ? 'Failed to record payment' : 'পেমেন্ট রেকর্ড ব্যর্থ', variant: 'destructive' });
      } finally {
        setSubmittingPayment(false);
      }
      return;
    }
    if (amount1 > invoice.due_amount) {
      toast({ title: language === 'en' ? 'Amount exceeds due balance' : 'পরিমাণ বকেয়ার চেয়ে বেশি', variant: 'destructive' });
      return;
    }
    setSubmittingPayment(true);
    try {
      const response = await axios.post(`${API}/invoices/${id}/payments`, {
        amount: amount1,
        payment_method: paymentMethod,
        notes: paymentNotes || null
      });
      setInvoice(response.data);
      setShowPaymentForm(false);
      setPaymentAmount('');
      setPaymentNotes('');
      toast({ title: language === 'en' ? 'Payment recorded!' : 'পেমেন্ট রেকর্ড হয়েছে!' });
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to record payment' : 'পেমেন্ট রেকর্ড ব্যর্থ', variant: 'destructive' });
    } finally {
      setSubmittingPayment(false);
    }
  };

  const paymentMethods = [
    { value: 'cash', label: language === 'en' ? 'Cash' : 'নগদ' },
    { value: 'bkash', label: 'bKash' },
    { value: 'nagad', label: 'Nagad' },
    { value: 'bank', label: language === 'en' ? 'Bank Transfer' : 'ব্যাংক ট্রান্সফার' },
    { value: 'card', label: language === 'en' ? 'Card' : 'কার্ড' },
  ];

  const garageName = garageInfo?.name || 'MotoPotti Garage';
  const garageNameBn = garageInfo?.name_bn || '';
  const garageAddress = garageInfo?.address || '';
  const garagePhone = garageInfo?.phone || '';

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!invoice) return null;

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`${basePath}/invoices`)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{invoice.invoice_number}</h1>
                <Badge className={`${statusColors[invoice.status]} border`}>
                  {statusLabelsMap[language]?.[invoice.status] || invoice.status}
                </Badge>
                {invoice.waived_amount > 0 && (
                  <Badge className="bg-orange-100 text-orange-800 border-orange-300 border">
                    {language === 'en' ? 'Waived' : 'মওকুফ'} {formatCurrency(invoice.waived_amount, language)}
                  </Badge>
                )}
              </div>
              <p className="text-slate-500">{formatDate(invoice.created_at, language)}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-pdf" title={t('download_pdf')}>
              <Download className="w-4 h-4 mr-1.5" />
              {t('download_pdf')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={sharing}
                  className="text-blue-700 border-blue-300 hover:bg-blue-50"
                  data-testid="share-invoice-btn"
                >
                  {sharing ? (
                    <Spinner className="w-4 h-4 mr-1.5" />
                  ) : (
                    <Share2 className="w-4 h-4 mr-1.5" />
                  )}
                  {sharing ? (shareProgress || t('share')) : t('share')}
                  {!sharing && <ChevronDown className="w-3 h-3 ml-1" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={openWhatsAppDialog} className="cursor-pointer">
                  <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
                  <span>{language === 'en' ? 'WhatsApp' : 'হোয়াটসঅ্যাপ'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareViaApps} className="cursor-pointer">
                  <Share2 className="w-4 h-4 mr-2 text-blue-600" />
                  <span>{language === 'en' ? 'Share via Apps' : 'অ্যাপে শেয়ার'}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {isPremium ? (
                  <>
                <DropdownMenuItem onClick={printThermal} className="cursor-pointer">
                  <Printer className="w-4 h-4 mr-2 text-slate-600" />
                  <span>{language === 'en' ? 'Thermal Print' : 'থার্মাল প্রিন্ট'}</span>
                </DropdownMenuItem>
                <div className="px-2 py-1.5 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <Settings2 className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500">{language === 'en' ? 'Paper:' : 'পেপার:'}</span>
                  <div className="flex gap-1 ml-auto">
                    <button
                      className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
                        thermalPaperSize === 58 ? 'bg-blue-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                      onClick={async () => {
                        setThermalPaperSize(58);
                        try { await axios.put(`${API}/settings/garage`, { thermal_paper_size: 58 }); } catch {}
                      }}
                    >58mm</button>
                    <button
                      className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
                        thermalPaperSize === 80 ? 'bg-blue-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                      onClick={async () => {
                        setThermalPaperSize(80);
                        try { await axios.put(`${API}/settings/garage`, { thermal_paper_size: 80 }); } catch {}
                      }}
                    >80mm</button>
                  </div>
                </div>
                  </>
                ) : (
                  <div
                    className="px-2 py-2 flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded-sm"
                    onClick={() => setUpgradePopup({ open: true, feature: language === 'en' ? 'Thermal Print' : 'থার্মাল প্রিন্ট' })}
                  >
                    <Printer className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-400">{language === 'en' ? 'Thermal Print' : 'থার্মাল প্রিন্ট'}</span>
                    <PremiumBadge size="sm" className="ml-auto" />
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="delete-invoice-btn"
              title={language === 'en' ? 'Delete' : 'মুছুন'}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {language === 'en' ? 'Delete Invoice?' : 'ইনভয়েস মুছে ফেলুন?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {language === 'en'
                  ? `This will permanently delete invoice ${invoice.invoice_number}. This action cannot be undone.`
                  : `${invoice.invoice_number} ইনভয়েস স্থায়ীভাবে মুছে যাবে। এর পরে ফেরত যাবে না।`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{language === 'en' ? 'Cancel' : 'বাতিল'}</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteInvoice}
                className="bg-red-600 hover:bg-red-700"
                disabled={deleting}
              >
                {deleting ? <Spinner size="sm" /> : (language === 'en' ? 'Delete' : 'মুছে ফেলুন')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* WhatsApp Phone Dialog */}
        <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {language === 'en' ? 'Send via WhatsApp' : 'হোয়াটসঅ্যাপে পাঠান'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-sm">{language === 'en' ? 'WhatsApp Number' : 'হোয়াটসঅ্যাপ নম্বর'}</Label>
                <Input
                  type="tel"
                  placeholder={language === 'en' ? 'Enter phone number' : 'ফোন নম্বর লিখুন'}
                  value={whatsAppPhone}
                  onChange={(e) => setWhatsAppPhone(e.target.value)}
                  className="mt-1"
                  autoFocus
                />
              </div>
              {/* Quick-select: primary phone */}
              {invoice?.customer_phone && invoice.customer_phone !== whatsAppPhone && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-left justify-start"
                  onClick={() => setWhatsAppPhone(invoice.customer_phone)}
                >
                  <User className="w-3.5 h-3.5 mr-2 text-slate-500" />
                  <span className="truncate">{invoice.customer_phone}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">{language === 'en' ? 'Primary' : 'প্রাথমিক'}</Badge>
                </Button>
              )}
              {/* Quick-select: additional saved numbers */}
              {additionalPhones
                .filter(p => p !== whatsAppPhone && p !== invoice?.customer_phone)
                .map((p) => (
                  <Button
                    key={p}
                    variant="outline"
                    size="sm"
                    className="w-full text-left justify-start"
                    onClick={() => setWhatsAppPhone(p)}
                  >
                    <Phone className="w-3.5 h-3.5 mr-2 text-slate-500" />
                    <span className="truncate">{p}</span>
                  </Button>
                ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWhatsAppDialog(false)}>
                {language === 'en' ? 'Cancel' : 'বাতিল'}
              </Button>
              <Button
                onClick={() => shareViaWhatsApp(whatsAppPhone)}
                disabled={!whatsAppPhone.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                {language === 'en' ? 'Send' : 'পাঠান'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Due Alert + Record Payment */}
        {invoice.due_amount > 0 && (
          <Card className="mb-6 border-2 border-orange-300 bg-orange-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                  <div>
                    <p className="font-semibold text-orange-800">
                      {language === 'en' ? 'Outstanding Due' : 'বকেয়া আছে'}
                    </p>
                    <p className="text-2xl font-bold text-orange-700">{formatCurrency(invoice.due_amount, language)}</p>
                  </div>
                </div>
                {!showPaymentForm && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setShowPaymentForm(true)}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="record-payment-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {language === 'en' ? 'Record Payment' : 'পেমেন্ট রেকর্ড'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowWaiveDialog(true)}
                      className="text-slate-600 border-slate-300 hover:bg-slate-100"
                      title={language === 'en' ? 'Waive off this due' : 'বকেয়া মওকুফ করুন'}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      {language === 'en' ? 'Waive Off' : 'মওকুফ'}
                    </Button>
                  </div>
                )}
              </div>

              {showPaymentForm && (
                <div className="mt-4 pt-4 border-t border-orange-200 space-y-3">
                  {/* Split Payment Toggle */}
                  <div className="flex items-center gap-2">
                    <Switch checked={splitPayment} onCheckedChange={(v) => { if (!isPremium) { setUpgradePopup({ open: true, feature: language === 'en' ? 'Split Payment' : 'বিভক্ত পেমেন্ট' }); return; } setSplitPayment(v); if (!v) setSplitAmount2(''); }} />
                    <Label className={`text-xs flex items-center gap-1 ${!isPremium ? 'text-slate-400 cursor-pointer' : 'text-orange-700'}`} onClick={() => { if (!isPremium) setUpgradePopup({ open: true, feature: language === 'en' ? 'Split Payment' : 'বিভক্ত পেমেন্ট' }); }}>
                      <Split className="w-3 h-3" />
                      {language === 'en' ? 'Split Payment (e.g. Cash + bKash)' : 'বিভক্ত পেমেন্ট (যেমন নগদ + বিকাশ)'}
                      {!isPremium && <PremiumBadge size="sm" className="ml-1" />}
                    </Label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-orange-700">{language === 'en' ? (splitPayment ? 'Amount 1' : 'Amount') : (splitPayment ? 'পরিমাণ ১' : 'পরিমাণ')}</Label>
                      <Input
                        type="number"
                        min="1"
                        max={invoice.due_amount}
                        step="1"
                        placeholder={`Max: ${invoice.due_amount}`}
                        value={paymentAmount}
                        onChange={(e) => {
                          const v = parseInt(e.target.value) || 0;
                          setPaymentAmount(v > invoice.due_amount ? invoice.due_amount : (e.target.value === '' ? '' : Math.max(0, v)));
                        }}
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-orange-700">{language === 'en' ? (splitPayment ? 'Method 1' : 'Payment Method') : (splitPayment ? 'পদ্ধতি ১' : 'পেমেন্ট পদ্ধতি')}</Label>
                      <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {paymentMethods.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-orange-700">{language === 'en' ? 'Note (optional)' : 'নোট (ঐচ্ছিক)'}</Label>
                      <Input
                        placeholder={language === 'en' ? 'Payment note...' : 'পেমেন্ট নোট...'}
                        value={paymentNotes}
                        onChange={(e) => setPaymentNotes(e.target.value)}
                      />
                    </div>
                  </div>
                  {/* Split Payment Row 2 */}
                  {splitPayment && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-orange-100/50 rounded-lg border border-orange-200">
                      <div>
                        <Label className="text-xs text-orange-700">{language === 'en' ? 'Amount 2' : 'পরিমাণ ২'}</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          placeholder={paymentAmount ? `Remaining: ${invoice.due_amount - (parseInt(paymentAmount) || 0)}` : ''}
                          value={splitAmount2}
                          onChange={(e) => {
                            const v = parseInt(e.target.value) || 0;
                            const max2 = invoice.due_amount - (parseInt(paymentAmount) || 0);
                            setSplitAmount2(v > max2 ? max2 : (e.target.value === '' ? '' : Math.max(0, v)));
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-orange-700">{language === 'en' ? 'Method 2' : 'পদ্ধতি ২'}</Label>
                        <Select value={splitMethod2} onValueChange={setSplitMethod2}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentMethods.filter(m => m.value !== paymentMethod).map((m) => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <p className="text-sm font-medium text-orange-700">
                          {language === 'en' ? 'Total' : 'মোট'}: {formatCurrency((parseInt(paymentAmount) || 0) + (parseInt(splitAmount2) || 0), language)}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    {!splitPayment && (
                      <Button
                        onClick={() => { setPaymentAmount(invoice.due_amount); }}
                        variant="outline"
                        size="sm"
                        className="text-orange-700 border-orange-300"
                      >
                        {language === 'en' ? 'Pay Full Due' : 'পুরো বকেয়া পরিশোধ'}
                      </Button>
                    )}
                    {splitPayment && (
                      <Button
                        onClick={() => {
                          const a1 = parseInt(paymentAmount) || 0;
                          if (a1 > 0) setSplitAmount2(invoice.due_amount - a1);
                          else { setPaymentAmount(Math.ceil(invoice.due_amount / 2)); setSplitAmount2(invoice.due_amount - Math.ceil(invoice.due_amount / 2)); }
                        }}
                        variant="outline"
                        size="sm"
                        className="text-orange-700 border-orange-300"
                      >
                        {language === 'en' ? 'Fill Remaining' : 'বাকি পূরণ'}
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={recordPayment}
                      disabled={submittingPayment || !paymentAmount || (splitPayment && !splitAmount2)}
                      className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                      data-testid="submit-payment"
                    >
                      {submittingPayment ? <Spinner size="sm" /> : (language === 'en' ? 'Confirm Payment' : 'পেমেন্ট নিশ্চিত')}
                    </Button>
                    <Button variant="outline" onClick={() => { setShowPaymentForm(false); setPaymentAmount(''); setPaymentNotes(''); setSplitPayment(false); setSplitAmount2(''); }}>
                      {language === 'en' ? 'Cancel' : 'বাতিল'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Invoice Preview */}
        <Card className="overflow-hidden">
          <div className="bg-blue-800 text-white p-6 text-center">
            <h2 className="text-2xl font-bold font-['Barlow_Condensed']">{garageName}</h2>
            {garageNameBn && <p className="text-blue-200">{garageNameBn}</p>}
            {garageAddress && <p className="text-blue-200 text-sm mt-1">{garageAddress}</p>}
            {garagePhone && <p className="text-blue-200 text-sm">{garagePhone}</p>}
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Invoice Info */}
            <div className="flex justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('invoice_number')}</p>
                <p className="font-bold text-lg">{invoice.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">{t('date')}</p>
                <p className="font-bold">{formatDate(invoice.created_at, language)}</p>
              </div>
            </div>

            {/* Customer & Bike Info */}
            <div className="grid grid-cols-2 gap-6 p-4 bg-slate-50 rounded-lg">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">{t('customer_name')}</span>
                </div>
                <p className="font-semibold">{invoice.customer_name}</p>
                <p className="text-sm text-slate-600">{invoice.customer_phone}</p>
                {invoice.customer_address && (
                  <p className="text-sm text-slate-600">{invoice.customer_address}</p>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Wrench className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">{t('bike_info')}</span>
                </div>
                <p className="font-semibold">{invoice.bike_info}</p>
                <p className="text-sm text-slate-600">
                  {t('job_number')}:{' '}
                  <Link to={`${basePath}/jobs/${invoice.job_id}`} className="text-blue-700 hover:underline font-medium">
                    {invoice.job_number}
                  </Link>
                </p>
              </div>
            </div>

            {/* Services */}
            {invoice.services?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">{t('services_selected')}</h3>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-sm text-slate-500">#</th>
                      <th className="text-left py-2 text-sm text-slate-500">{language === 'en' ? 'Service' : 'সার্ভিস'}</th>
                      <th className="text-right py-2 text-sm text-slate-500">{t('price')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.services.map((service, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-3">{index + 1}</td>
                        <td className="py-3 font-medium">
                          {service.name}
                          {service.is_custom && (
                            <span className="ml-2 text-xs text-slate-400">({language === 'en' ? 'Custom' : 'কাস্টম'})</span>
                          )}
                        </td>
                        <td className="py-3 text-right">{formatCurrency(service.price, language)}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50">
                      <td colSpan={2} className="py-3 font-semibold">{language === 'en' ? 'Services Subtotal' : 'সার্ভিস উপমোট'}</td>
                      <td className="py-3 text-right font-bold">{formatCurrency(invoice.subtotal_services, language)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Parts */}
            {invoice.parts?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">{t('parts_used')}</h3>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-sm text-slate-500">#</th>
                      <th className="text-left py-2 text-sm text-slate-500">{language === 'en' ? 'Part' : 'পার্ট'}</th>
                      <th className="text-center py-2 text-sm text-slate-500">{language === 'en' ? 'Qty' : 'সংখ্যা'}</th>
                      <th className="text-right py-2 text-sm text-slate-500">{t('price')}</th>
                      <th className="text-right py-2 text-sm text-slate-500">{t('total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.parts.map((part, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-3">{index + 1}</td>
                        <td className="py-3 font-medium">
                          {part.part_name}
                          {part.is_custom && (
                            <span className="ml-2 text-xs text-slate-400">({language === 'en' ? 'Custom' : 'কাস্টম'})</span>
                          )}
                        </td>
                        <td className="py-3 text-center">{part.quantity}</td>
                        <td className="py-3 text-right">{formatCurrency(part.unit_price, language)}</td>
                        <td className="py-3 text-right">{formatCurrency(part.quantity * part.unit_price, language)}</td>
                      </tr>
                    ))}
                    <tr className="bg-orange-50">
                      <td colSpan={4} className="py-3 font-semibold">{language === 'en' ? 'Parts Subtotal' : 'পার্টস উপমোট'}</td>
                      <td className="py-3 text-right font-bold">{formatCurrency(invoice.subtotal_parts, language)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals */}
            <div className="p-4 bg-slate-900 text-white rounded-lg">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">{t('subtotal')}</span>
                  <span>{formatCurrency(invoice.subtotal, language)}</span>
                </div>
                {invoice.discount_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('discount')}</span>
                    <span className="text-red-400">-{formatCurrency(invoice.discount_amount, language)}</span>
                  </div>
                )}
                {invoice.points_discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">{language === 'en' ? `Points Discount (${invoice.points_redeemed || 0} pts)` : `পয়েন্ট ডিসকাউন্ট (${invoice.points_redeemed || 0} পয়েন্ট)`}</span>
                    <span className="text-yellow-400">-{formatCurrency(invoice.points_discount, language)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-slate-700">
                  <span className="font-bold text-lg">{t('total')}</span>
                  <span className="text-2xl font-bold">{formatCurrency(invoice.total, language)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">{t('paid_amount')}</span>
                  <span className="text-green-400">{formatCurrency(invoice.paid_amount, language)}</span>
                </div>
                {invoice.due_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('due_amount')}</span>
                    <span className="text-red-400">{formatCurrency(invoice.due_amount, language)}</span>
                  </div>
                )}
                {invoice.waived_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-orange-500">{language === 'en' ? 'Waived Off' : 'মওকুফ'}</span>
                    <span className="text-orange-500">{formatCurrency(invoice.waived_amount, language)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment History */}
            {invoice.payment_history?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  {language === 'en' ? 'Payment History' : 'পেমেন্ট ইতিহাস'}
                </h3>
                <div className="space-y-2">
                  {invoice.payment_history.map((p, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${
                      p.payment_method === 'waived' ? 'bg-orange-50 border-orange-100' : 'bg-green-50 border-green-100'
                    }`}>
                      <div className="flex items-center gap-3">
                        {p.payment_method === 'waived' ? (
                          <XCircle className="w-4 h-4 text-orange-600" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        )}
                        <div>
                          {p.payment_method === 'waived' ? (
                            <span className="font-medium text-orange-700">{language === 'en' ? 'Due Waived Off' : 'বকেয়া মওকুফ'}</span>
                          ) : (
                            <>
                              <span className="font-medium">{formatCurrency(p.amount, language)}</span>
                              <span className="text-sm text-slate-500 ml-2">
                                via {paymentMethodLabels[p.payment_method] || p.payment_method}
                              </span>
                            </>
                          )}
                          {p.notes && <p className="text-xs text-slate-400">{p.notes}</p>}
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">{p.date ? formatDate(p.date, language) : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Info (legacy) */}
            {(!invoice.payment_history || invoice.payment_history.length === 0) && invoice.payment_method && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>{t('payment_method')}: {paymentMethodLabels[invoice.payment_method] || invoice.payment_method}</span>
              </div>
            )}

            {/* Loyalty summary (subtle, below payments) */}
            {(invoice.points_earned > 0 || customerPoints?.current_balance !== undefined) && (
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-500">
                <div className="flex items-center justify-between">
                  <span>{language === 'en' ? 'Points Earned' : 'অর্জিত পয়েন্ট'}</span>
                  <span className="text-slate-600">+{invoice.points_earned || 0} {language === 'en' ? 'pts' : 'পয়েন্ট'}</span>
                </div>
                {customerPoints?.current_balance !== undefined && (
                  <div className="flex items-center justify-between mt-1">
                    <span>{language === 'en' ? 'Current Points Balance' : 'বর্তমান পয়েন্ট ব্যালেন্স'}</span>
                    <span className="text-slate-700 font-medium">{customerPoints.current_balance} {language === 'en' ? 'pts' : 'পয়েন্ট'}</span>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {invoice.notes && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">{t('notes')}</p>
                <p className="text-slate-600">{invoice.notes}</p>
              </div>
            )}

            {/* Footer */}
            <div className="text-center pt-6 border-t">
              <p className="text-slate-600">{language === 'en' ? 'Thank you for your business!' : 'আপনার ব্যবসার জন্য ধন্যবাদ!'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Waive Confirmation Dialog */}
      <AlertDialog open={showWaiveDialog} onOpenChange={setShowWaiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'en' ? 'Waive Off This Due?' : 'বকেয়া মওকুফ করবেন?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'en'
                ? `This will forgive the remaining due of ${formatCurrency(invoice?.due_amount || 0, 'en')}. The invoice will be marked as paid with a "Waived" badge. The waived amount will be recorded in payment history.`
                : `এটি অবশিষ্ট ${formatCurrency(invoice?.due_amount || 0, 'bn')} বকেয়া মওকুফ করবে। ইনভয়েসটি "মওকুফ" ব্যাজসহ পরিশোধিত হিসেবে চিহ্নিত হবে। মওকুফকৃত পরিমাণ পেমেন্ট ইতিহাসে রেকর্ড হবে।`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={waiving}>
              {language === 'en' ? 'Cancel' : 'বাতিল'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={waiveInvoiceDue}
              disabled={waiving}
              className="bg-slate-700 hover:bg-slate-800"
            >
              {waiving ? <Spinner size="sm" /> : (language === 'en' ? 'Waive Off' : 'মওকুফ')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PremiumUpgradeDialog
        open={upgradePopup.open}
        onOpenChange={(v) => setUpgradePopup(prev => ({ ...prev, open: v }))}
        feature={upgradePopup.feature}
      />
    </AdminLayout>
  );
}
